import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Heart, ListMusic, Pause, Play, Share2, SkipBack, SkipForward, Volume2, VolumeX, Sparkles, X } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { fmtTime } from "../lib/api";
import { Cover } from "./MixCard";
import { FxGrid } from "./FxGrid";
import { Waveform } from "./Waveform";
import { useT } from "../lib/i18n";

const Visualizer = () => {
  const { graphRef, playing } = usePlayer();
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return undefined;
    const g = c.getContext("2d");
    let id;
    const draw = () => {
      const an = graphRef.current?.analyser;
      const n = 40;
      const data = new Uint8Array(an ? an.frequencyBinCount : n);
      if (an) an.getByteFrequencyData(data);
      g.clearRect(0, 0, c.width, c.height);
      const w = c.width / n;
      for (let i = 0; i < n; i++) {
        const v = an && playing ? data[i] / 255 : 0.06;
        const h = Math.max(2, v * c.height);
        g.fillStyle = i % 5 === 0 ? "#FFFFFF" : "#1FD1FF";
        g.fillRect(i * w + 1, c.height - h, w - 2, h);
      }
      id = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(id);
  }, [graphRef, playing]);
  return <canvas ref={ref} width={120} height={32} className="hidden xl:block w-28 h-8" data-testid="player-visualizer" />;
};

const IconBtn = ({ children, testid, onClick, label, className = "" }) => (
  <button data-testid={testid} onClick={onClick} aria-label={label} className={`p-2 text-[#A8B3C4] hover:text-white transition-colors ${className}`}>{children}</button>
);

const Panel = ({ testid, title, onClose, closeId, children }) => (
  <motion.div data-testid={testid} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
    transition={{ duration: 0.25 }} className="overflow-hidden border-t border-white/10">
    <div className="max-w-2xl mx-auto px-4 py-4 max-h-[55vh] overflow-y-auto" data-lenis-prevent>
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-2xl">{title}</p>
        <IconBtn testid={closeId} onClick={onClose} label="Close"><X className="w-4 h-4" /></IconBtn>
      </div>
      {children}
    </div>
  </motion.div>
);

const QueueList = () => {
  const { queue, mix, playMix, playing } = usePlayer();
  const { t } = useT();
  if (!queue.length) return <p className="font-mono text-xs text-[#A8B3C4]">{t("queue.empty")}</p>;
  return (
    <ol className="divide-y divide-white/5">
      {queue.map((q, i) => {
        const cur = q.id === mix?.id;
        return (
          <li key={q.id}>
            <button data-testid={`queue-item-${i + 1}`} onClick={() => playMix(q, queue)} className={`w-full flex items-center gap-3 py-2.5 text-left transition-colors ${cur ? "text-[#1FD1FF]" : "hover:text-[#1FD1FF]"}`}>
              <span className="font-mono text-xs w-6 text-[#A8B3C4]">{cur && playing ? "\u25B6" : String(i + 1).padStart(2, "0")}</span>
              <span className="w-9 h-9 shrink-0 overflow-hidden"><Cover mix={q} /></span>
              <span className="min-w-0 flex-1"><span className="block font-semibold text-sm truncate">{q.title}</span><span className="block font-mono text-[11px] text-[#A8B3C4]">{q.genre}</span></span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

export const BottomPlayer = () => {
  const { mix, playing, toggle, time, duration, seek, step, volume, setVolume, likeMix, setShareMix, downloadMix, fxOpen, setFxOpen, audioRef, bpm } = usePlayer();
  const [queueOpen, setQueueOpen] = useState(false);
  const { t } = useT();
  const openFx = () => { setQueueOpen(false); setFxOpen(!fxOpen); };
  const openQueue = () => { setFxOpen(false); setQueueOpen(!queueOpen); };

  return (
    <AnimatePresence>
      {mix && (
        <motion.div data-testid="bottom-player" initial={{ y: 140 }} animate={{ y: 0 }} exit={{ y: 140 }}
          transition={{ type: "spring", stiffness: 200, damping: 26 }} className="fixed bottom-0 inset-x-0 z-50">
          <div className="bg-[#050B16]/95 backdrop-blur-2xl border-t border-white/10 shadow-[0_-10px_30px_rgba(0,0,0,0.8)] pb-[env(safe-area-inset-bottom)]">
            <div className="max-w-[1500px] mx-auto px-3 lg:px-8 pt-2.5 pb-1 flex items-center gap-2 sm:gap-3">
              <IconBtn testid="player-queue-button" onClick={openQueue} label={t("queue.title")} className={queueOpen ? "!text-[#1FD1FF]" : ""}><ListMusic className="w-6 h-6" /></IconBtn>
              <div className="min-w-0 flex-1">
                <p data-testid="player-title" className="font-semibold text-[15px] sm:text-base leading-tight truncate">{mix.title}</p>
                <p className="font-mono text-[11px] text-[#A8B3C4] truncate">DJ T-REX{mix.genre ? ` \u00b7 ${mix.genre}` : ""}</p>
              </div>
              <Visualizer />
              <div className="hidden md:flex items-center">
                <IconBtn testid="player-like-button" onClick={() => likeMix(mix)} label="Like"><Heart className={`w-4 h-4 ${mix.liked ? "fill-[#FFFFFF] text-[#FFFFFF]" : ""}`} /></IconBtn>
                <IconBtn testid="player-share-button" onClick={() => setShareMix(mix)} label="Share"><Share2 className="w-4 h-4" /></IconBtn>
                <IconBtn testid="player-download-button" onClick={() => downloadMix(mix)} label="Download"><Download className="w-4 h-4" /></IconBtn>
                <IconBtn testid="player-mute-button" onClick={() => setVolume(volume ? 0 : 0.85)} label="Mute">{volume ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</IconBtn>
                <input data-testid="player-volume-slider" type="range" className="scrub w-20 lg:w-24" min={0} max={1} step={0.01} value={volume}
                  style={{ "--p": `${volume * 100}%` }} onChange={(e) => setVolume(Number(e.target.value))} aria-label="Volume" />
              </div>
              <IconBtn testid="sound-fx-pad-toggle" onClick={openFx} label="Sound FX" className={fxOpen ? "!text-[#1FD1FF]" : ""}><Sparkles className="w-5 h-5" /></IconBtn>
              <div className="flex items-center">
                <IconBtn testid="player-prev-button" onClick={() => step(-1)} label="Previous" className="!text-white"><SkipBack className="w-6 h-6 fill-current" /></IconBtn>
                <button data-testid="player-play-pause-button" onClick={toggle} aria-label={playing ? "Pause" : "Play"}
                  className="w-11 h-11 rounded-full bg-[#1FD1FF] text-black grid place-items-center hover:scale-105 transition-transform">
                  {playing ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
                </button>
                <IconBtn testid="player-next-button" onClick={() => step(1)} label="Next" className="!text-white"><SkipForward className="w-6 h-6 fill-current" /></IconBtn>
              </div>
            </div>
            <div className="max-w-[1500px] mx-auto px-4 lg:px-8 pb-3 flex items-end gap-3">
              <span data-testid="player-time" className="font-mono text-sm tabular-nums w-12 pb-1.5">{fmtTime(time)}</span>
              <Waveform mix={mix} time={time} duration={duration} onSeek={seek} audioRef={audioRef} bpm={bpm} />
              <span data-testid="player-duration" className="font-mono text-sm tabular-nums w-12 text-right pb-1.5">{fmtTime(duration)}</span>
            </div>
            <AnimatePresence initial={false}>
              {fxOpen && <Panel testid="fx-panel" closeId="fx-panel-close" title={t("fx.panel")} onClose={() => setFxOpen(false)}><FxGrid compact /></Panel>}
              {queueOpen && <Panel testid="queue-panel" closeId="queue-panel-close" title={t("queue.title")} onClose={() => setQueueOpen(false)}><QueueList /></Panel>}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
